// ───────────────────────────────────────────────────────────────
//  dedupeMarkers.js  – v7 (refactored with Name_Buckets_A/B and Geo_Buckets_A/B)
//  Changes:
//  - Added Name_Buckets_A and Geo_Buckets_A for initial grouping and coordinate refinement.
//  - Added Name_Buckets_B and Geo_Buckets_B for final deduplication.
//  - Replaced displacement logic with clustering for identical coordinates.
//  - Extensive debug logs added for monitoring flow.
// ───────────────────────────────────────────────────────────────

const SIM_THRESHOLD = 0.88;

// /* ── Helper: Calculate Refined Coordinates ───────────────────── */
// function refineCoordinates(group) {
//   console.log(`[REFINE] Refining coordinates for group of size ${group.length}`);
//   // Placeholder logic for refining coordinates (can be replaced with actual logic)
//   return group.map((loc, idx) => {
//     loc.lat = parseFloat((loc.lat + idx * 0.00001).toFixed(6));
//     loc.lng = parseFloat((loc.lng + idx * 0.00001).toFixed(6));
//     return loc;
//   });
// }

/* ── Ship-date filter: drop rows shipped > 2 years ago ───────────── */
export function filterOldShipDates(data) {
  const TWO_YEARS_MS = 2 * 365 * 24 * 60 * 60 * 1000;   // ≈ 24 months
  const totalBefore = data.length
  data = data.filter(loc => {
    if (!loc.lastShipDate) return true;            // keep Prospects / no date
    const ts = Date.parse(loc.lastShipDate);       // expects ISO yyyy-mm-dd
    if (isNaN(ts)) return true;            // bad date → keep
    return (Date.now() - ts) <= TWO_YEARS_MS;      // keep if ≤ 2 years
  });

  const removed = totalBefore - data.length;
  console.log(`Shipdate filter Results showing ${data.length} of ${totalBefore} rows removed ${removed}`);
  return data
};




export function cleanLeads(locations = []) {
  const totalBefore = locations.length;

  // Step 1: Basic filtering (invalid name, dummy name)
  let cleaned = locations
    .filter(loc => {
      if (!loc.name || loc.name.trim() === '' || loc.name == 'N/A') return false;
      if (loc.name.toLowerCase().includes('dummy')) return false;
      return true;
    })
    .map(loc => {
      if (loc.address) {
        loc.address = loc.address.replace(/\s*null\s*/gi, ' ').trim();
      }
      return loc;
    });

  const removedForName = totalBefore - cleaned.length;

  // Step 2: De-duplicate by name + lat/lng — keep latest createdDate
  const dedupedMap = new Map(); // key = "lat,lng|name"

  cleaned.forEach(loc => {
    const key = `${loc.lat},${loc.lng}|${loc.name.trim().toLowerCase()}`;
    const existing = dedupedMap.get(key);
    const currentCreated = new Date(loc.createdDate || 0);
    const existingCreated = new Date(existing?.createdDate || 0);

    if (!existing || currentCreated > existingCreated) {
      dedupedMap.set(key, loc);
    }
  });

  const finalCleaned = Array.from(dedupedMap.values());
  const removedForDupes = cleaned.length - finalCleaned.length;

  console.log(`cleanLeads:`);
  console.log(`- Discarded ${removedForName} of ${totalBefore} due to invalid/dummy names`);
  console.log(`- Removed ${removedForDupes} duplicate leads with same name and lat/lng`);
  console.log(`- Final lead count: ${finalCleaned.length}`);
  console.log('- Final leads:', finalCleaned);
  return finalCleaned;
}


/* --- tiny Jaro similarity (0-1) -------------------------------- */
function jaro(a, b) {
  a = a.toLowerCase(); b = b.toLowerCase();
  const m = Math.floor(Math.max(a.length, b.length) / 2) - 1;
  const fa = Array(a.length).fill(false);
  const fb = Array(b.length).fill(false);
  let matches = 0, trans = 0;

  for (let i = 0; i < a.length; i++) {
    const from = Math.max(0, i - m);
    const to = Math.min(i + m + 1, b.length);
    for (let j = from; j < to; j++) {
      if (!fb[j] && a[i] === b[j]) { fa[i] = fb[j] = true; matches++; break; }
    }
  }
  if (!matches) return 0;

  for (let i = 0, k = 0; i < a.length; i++)
    if (fa[i]) {
      while (!fb[k]) k++;
      if (a[i] !== b[k]) trans++;
      k++;
    }
  return (matches / a.length +
    matches / b.length +
    (matches - trans / 2) / matches) / 3;
}



// /**
// * Displace points with identical lat/lng by ~5 meters apart.
// * @param {Array<{lat:number,lng:number,[key:string]:any}>} locations
// * @param {number} spacingMeters - how far apart to spread them (default: 5 m)
// * @returns same array with displaced lat/lng for duplicates
// */
export function displaceDuplicates(locations, spacingMeters = 5) {
  const R = 6378137; // earth radius in meters (WGS84)
  const grouped = new Map();

  // 1. group by lat,lng
  locations.forEach((loc, i) => {
    const key = `${loc.lat},${loc.lng}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push({ ...loc, _originalIndex: i });
  });

  // 2. displace only groups with >1
  for (const [key, group] of grouped.entries()) {
    if (group.length <= 1) continue;


    const angleStep = (2 * Math.PI) / group.length; // circle layout
    group.forEach((loc, idx) => {
      const angle = idx * angleStep;
      const dLat = (spacingMeters * Math.cos(angle)) / R;
      const dLng = (spacingMeters * Math.sin(angle)) / (R * Math.cos(loc.lat * Math.PI / 180));

      loc.lat = loc.lat + (dLat * 180) / Math.PI;
      loc.lng = loc.lng + (dLng * 180) / Math.PI;
      loc._displaced = true;
    });

    // write back to original array
    group.forEach((loc) => {
      locations[loc._originalIndex] = loc;
      delete loc._originalIndex;
    });
  }

  return locations;
}

/* --- newest by date helper (null ⇒ -∞) ------------------------ */
// function newest(rows) {
//   return rows.reduce((best, cur) => {
//     const dBest = best?.lastShipDate ? new Date(best.lastShipDate) : -Infinity;
//     const dCur = cur.lastShipDate ? new Date(cur.lastShipDate) : -Infinity;
//     return dCur > dBest ? cur : best;
//   }, null);
// }

function displaceLatLng(lat, lng, index, total, radiusInMeters = 8) {
  const angle = (2 * Math.PI / total) * index;
  const latOffset = (radiusInMeters / 111000) * Math.cos(angle);
  const lngOffset = (radiusInMeters / (111000 * Math.cos(lat * Math.PI / 180))) * Math.sin(angle);
  return [lat + latOffset, lng + lngOffset];
}


/* --- business unit accessor ---------------------------------- */
function getBU(row) {
  return (
    row?.accountDetails?.businessUnit ??
    row?.businessUnit ??
    row?.business_unit ??
    ''
  ) || '';
}



// Assumes you already have these somewhere:
// - jaro(a,b)
// - SIM_THRESHOLD (ex: 0.88)
// - displaceLatLng(lat, lng, index, total, radiusMeters)
// If not, keep your existing ones exactly as-is.

const norm = (s) => (s ?? "").toString().trim();
const up = (s) => norm(s).toUpperCase();
const coordKey = (lat, lng, dp = 6) => `${Number(lat).toFixed(dp)},${Number(lng).toFixed(dp)}`;

// const getBU = (r) => norm(r.businessUnit) || "(none)";

/** newest by ship date, fallback to lastActivity, fallback createdDate */
function newest(rows) {
  const toTime = (v) => {
    if (!v) return -Infinity;
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : -Infinity;
  };

  return rows.reduce((best, cur) => {
    const bestShip = toTime(best?.lastShipDate);
    const curShip = toTime(cur?.lastShipDate);
    if (curShip !== bestShip) return curShip > bestShip ? cur : best;

    const bestAct = toTime(best?.lastActivity);
    const curAct = toTime(cur?.lastActivity);
    if (curAct !== bestAct) return curAct > bestAct ? cur : best;

    const bestCreated = toTime(best?.createdDate);
    const curCreated = toTime(cur?.createdDate);
    return curCreated > bestCreated ? cur : best;
  }, rows[0]);
}

/** cluster rows by name similarity */
function clusterByName(rows, simThreshold) {
  const clusters = [];
  for (const r of rows) {
    const name = norm(r.name);
    let placed = false;
    for (const c of clusters) {
      // compare to representative (first element) for speed/stability
      const rep = norm(c[0].name);
      if (jaro(rep, name) >= simThreshold) {
        c.push(r);
        placed = true;
        break;
      }
    }
    if (!placed) clusters.push([r]);
  }
  return clusters;
}

/** pick anchor geo bucket inside a name cluster: fewer rows => higher accuracy heuristic */
function pickAnchorCoordKeyForNameCluster(cluster, geoBuckets, dp = 6) {
  // Build set of coord keys that this cluster spans
  const keys = new Map(); // key -> count in this cluster
  for (const r of cluster) {
    const k = coordKey(r.lat, r.lng, dp);
    keys.set(k, (keys.get(k) || 0) + 1);
  }

  // Choose the coord key whose *global geo bucket* size is smallest.
  // tie-breaker: the key that appears most within this name cluster.
  let best = null;
  for (const [k, withinCount] of keys.entries()) {
    const globalSize = geoBuckets.get(k)?.length ?? Infinity;
    const score = { globalSize, withinCount };
    if (!best) {
      best = { k, ...score };
      continue;
    }
    if (score.globalSize < best.globalSize) best = { k, ...score };
    else if (score.globalSize === best.globalSize && score.withinCount > best.withinCount) best = { k, ...score };
  }
  return best?.k || coordKey(cluster[0].lat, cluster[0].lng, dp);
}

/**
* Main function:
* 1) Build global geo buckets (original coords).
* 2) Build global name clusters.
* 3) For each name cluster, pick anchor coordKey using geo bucket "fewest rows" heuristic.
*    -> write refactoredLat/refactoredLng onto each row (marker coords).
* 4) Dedupe within (refactored coords + name-sim cluster) by BU and status priority.
* 5) Final displacement ONLY when same refactored coords have 2+ DIFFERENT name clusters.
*
* Logging:
* - concise summary always when debug=true
* - detailed drop/coord-change logs are capped (logLimit)
*/
export function dedupeMarkers(
  data,
  {
    debug = false,
    logLimit = 40,
    coordDp = 6,
    displacementRadiusMeters = 8,
    simThreshold = SIM_THRESHOLD,
  } = {}
) {
  const t0 = debug ? performance.now() : 0;

  // -----------------------
  // helpers for logging
  // -----------------------
  const stats = {
    input: data.length,
    nameClusters: 0,
    coordsChanged: 0,
    dedupeDrops: 0,
    keep: 0,
    displaced: 0,
    inactiveStatusDropped: 0,
  };

  const sample = {
    coordChanges: [],
    drops: [],
    disp: [],
  };

  const pushSample = (arr, item) => {
    if (arr.length < logLimit) arr.push(item);
  };

  // ----------------------------------------------------
  // Stage A: global geo buckets (original coordinates)
  // ----------------------------------------------------
  const geoBuckets = new Map(); // coordKey(original) -> rows
  for (const r of data) {
    const k = coordKey(r.lat, r.lng, coordDp);
    (geoBuckets.get(k) || geoBuckets.set(k, []).get(k)).push(r);
  }

  // -------------------------------------------
  // Stage B: global name buckets (all rows)
  // -------------------------------------------
  const nameBuckets = clusterByName(data, simThreshold);
  if (debug) {
    console.log(`[DEDUP] formed ${nameBuckets.length} name clusters from ${data.length} rows (sim=${simThreshold})`);
    nameBuckets.slice(0, 500).forEach((cluster, i) => {
      const rep = cluster[0] || {};
      const repName = (rep.name || "").trim();
      const sampleIds = cluster.slice(0, 3).map(r => r.sfId || r.accountId || r.name).join(', ');
      console.log(`  [cluster ${i + 1}/${nameBuckets.length}] size=${cluster.length} rep="${repName}" sample=${sampleIds}`);
    });
    if (nameBuckets.length > logLimit) {
      console.log(`  ... ${nameBuckets.length - logLimit} more clusters (omitted)`);
    }
  } else {
    console.log(`[DEDUP] formed ${nameBuckets.length} name clusters from ${data.length} rows`);
  }
  stats.nameClusters = nameBuckets.length;

  // ---------------------------------------------------------
  // Stage C: refactor coords using anchor geo bucket per name
  // ---------------------------------------------------------
  for (const bucket of nameBuckets) {
    if (bucket.length <= 1) {
      // still assign refactored coords (same as original) for consistency
      const r = bucket[0];
      r.refactoredLat = r.lat;
      r.refactoredLng = r.lng;
      continue;
    }

    const anchorKey = pickAnchorCoordKeyForNameCluster(bucket, geoBuckets, coordDp);
    const [aLatStr, aLngStr] = anchorKey.split(",");
    const aLat = Number(aLatStr);
    const aLng = Number(aLngStr);

    for (const r of bucket) {
      const before = coordKey(r.lat, r.lng, coordDp);
      const after = coordKey(aLat, aLng, coordDp);

      r.refactoredLat = aLat;
      r.refactoredLng = aLng;

      if (before !== after) {
        stats.coordsChanged++;
        if (debug) {
          pushSample(sample.coordChanges, {
            name: r.name,
            sfId: r.sfId || r.accountId || "",
            from: before,
            to: after,
            reason: "name-cluster anchor coordKey",
          });
        }
      }
    }
  }

  // ---------------------------------------------------------
  // Stage D: dedupe within (ref coords + name-sim cluster)
  // ---------------------------------------------------------
  // First group by refactored coords
  const refGeo = new Map(); // coordKey(ref) -> rows
  for (const r of data) {
    const k = coordKey(r.refactoredLat ?? r.lat, r.refactoredLng ?? r.lng, coordDp);
    (refGeo.get(k) || refGeo.set(k, []).get(k)).push(r);
  }

  const survivors = [];

  for (const [refKey, rowsAtRef] of refGeo.entries()) {
    // Cluster by name similarity INSIDE this coordinate bucket
    const nClusters = clusterByName(rowsAtRef, simThreshold);

    for (const cluster of nClusters) {
      if (cluster.length === 1) {
        survivors.push(cluster[0]);
        continue;
      }

      // Split by business unit
      const byBU = new Map();
      for (const r of cluster) {
        const bu = getBU(r);
        (byBU.get(bu) || byBU.set(bu, []).get(bu)).push(r);
      }

      for (const [bu, buRows] of byBU.entries()) {
        // ---- NEW CONDITION:
        // If status === 'inactive' and there exists ACTIVE or ACTIVEOLDER (same name cluster + same location)
        // then drop those inactive-status rows.
        const hasActiveOrOlder = buRows.some((r) => ["ACTIVE", "ACTIVEOLDER"].includes(up(r.shipStatus)));
        const inactiveStatusRows = buRows.filter((r) => up(r.status) === "INACTIVE");
        let filteredRows = buRows;

        if (inactiveStatusRows.length > 0 && hasActiveOrOlder) {
          filteredRows = buRows.filter((r) => up(r.status) !== "INACTIVE");
          stats.inactiveStatusDropped += inactiveStatusRows.length;
          stats.dedupeDrops += inactiveStatusRows.length;
          if (debug) {
            inactiveStatusRows.forEach((r) =>
              pushSample(sample.drops, {
                name: r.name,
                sfId: r.sfId || r.accountId || "",
                bu,
                refKey,
                drop: "status=INACTIVE",
                keptBecause: "ACTIVE/ACTIVEOLDER exists in same name+location",
              })
            );
          }
        }

        const U = (x) => up(x?.shipStatus ?? x?.status);
 

        const actives = filteredRows.filter((r) => U(r) === "ACTIVE");
        const prospects = filteredRows.filter((r) => U(r) === "PROSPECT");
        const activeOlder = filteredRows.filter((r) => U(r) === "ACTIVEOLDER");

        // Apply your existing rules (unchanged behavior, just using filteredRows)
        if (actives.length) {
          const ch = newest(actives);
          survivors.push(ch);
          if (debug) {
            filteredRows.forEach((r) => {
              if (r !== ch) {
                stats.dedupeDrops++;
                pushSample(sample.drops, {
                  name: r.name,
                  sfId: r.sfId || r.accountId || "",
                  bu,
                  refKey,
                  drop: `${U(r.shipStatus) || "(blank)"}`,
                  kept: `ACTIVE newest ship/activity (${ch.sfId || ch.accountId || ch.name})`,
                });
              }
            });
          } else {
            // still count drops even if not logging
            stats.dedupeDrops += Math.max(0, filteredRows.length - 1);
          }
          continue;
        }

        if (prospects.length && !activeOlder.length) {
          // keep all prospects
          survivors.push(...prospects);
          // (no drops)
          continue;
        }

        if (prospects.length && activeOlder.length) {
          // keep prospects, drop activeOlder
          survivors.push(...prospects);
          if (debug) {
            activeOlder.forEach((r) => {
              stats.dedupeDrops++;
              pushSample(sample.drops, {
                name: r.name,
                sfId: r.sfId || r.accountId || "",
                bu,
                refKey,
                drop: "ACTIVEOLDER",
                kept: `PROSPECT (${prospects.length})`,
              });
            });
          } else {
            stats.dedupeDrops += activeOlder.length;
          }
          continue;
        }

        if (activeOlder.length) {
          const ch = newest(activeOlder);
          survivors.push(ch);
          if (debug) {
            activeOlder.forEach((r) => {
              if (r !== ch) {
                stats.dedupeDrops++;
                pushSample(sample.drops, {
                  name: r.name,
                  sfId: r.sfId || r.accountId || "",
                  bu,
                  refKey,
                  drop: "ACTIVEOLDER older",
                  kept: `ACTIVEOLDER newest ship/activity (${ch.sfId || ch.accountId || ch.name})`,
                });
              }
            });
          } else {
            stats.dedupeDrops += Math.max(0, activeOlder.length - 1);
          }
          continue;
        }

        // fallback: keep all (unknown statuses)
        survivors.push(...filteredRows);
      }
    }
  }

  // ----------------------------------------------
  // Stage E: final displacement ONLY when
  // same ref coords have 2+ *different* name clusters
  // ----------------------------------------------
  const groupsByRefCoord = new Map();
  for (const r of survivors) {
    const k = coordKey(r.refactoredLat ?? r.lat, r.refactoredLng ?? r.lng, coordDp);
    (groupsByRefCoord.get(k) || groupsByRefCoord.set(k, []).get(k)).push(r);
  }

  const finalRows = [];


  for (const [k, group] of groupsByRefCoord.entries()) {
    if (group.length === 1) {
      // set marker coords from refactored coords
      const g0 = group[0];
      finalRows.push({
        ...g0,
        lat: g0.refactoredLat ?? g0.lat,
        lng: g0.refactoredLng ?? g0.lng,
      });
      continue;
    }

    // cluster by name similarity inside same coord group
    const nameClustersHere = clusterByName(group, simThreshold);

    if (nameClustersHere.length === 1) {
      // Names are similar -> DO NOT DISPLACE.
      // Keep at exact same coords; later you'll "stack/group" UI with count.
      for (const r of group) {
        finalRows.push({
          ...r,
          lat: r.refactoredLat ?? r.lat,
          lng: r.refactoredLng ?? r.lng,
        });
      }
      continue;
    }

    // Names are NOT similar (multiple clusters at same coords) -> DISPLACE BY CLUSTER (not by row)
    // Stable ordering to prevent flicker across renders

    const orderedClusters = [...nameClustersHere].sort((a, b) => {
      const a0 = a[0] || {};
      const b0 = b[0] || {};
      const idA = (a0.sfId || a0.accountId || a0.name || "").toString();
      const idB = (b0.sfId || b0.accountId || b0.name || "").toString();
      return idA.localeCompare(idB);
    });

    const baseLat = (group[0].refactoredLat ?? group[0].lat);
    const baseLng = (group[0].refactoredLng ?? group[0].lng);

    // Displace each cluster as a whole; keep rows within cluster stacked
    orderedClusters.forEach((cluster, clusterIdx) => {
      const [lat, lng] = displaceLatLng(
        baseLat,
        baseLng,
        clusterIdx,
        orderedClusters.length,
        displacementRadiusMeters
      );

      const moved = clusterIdx > 0;
      if (moved) stats.displaced += cluster.length;

      if (debug && moved) {
        // log once per cluster (not per row)
        pushSample(sample.disp, {
          coord: k,
          name: (cluster[0]?.name || "").trim(),
          sfId: cluster[0]?.sfId || cluster[0]?.accountId || "",
          to: coordKey(lat, lng, coordDp),
          reason: `same coords, different name clusters (cluster ${clusterIdx + 1}/${orderedClusters.length}, moved ${cluster.length})`,
        });
      }

      cluster.forEach((r) => {
        finalRows.push({
          ...r,
          lat,
          lng,
          _displaced: moved ? true : r._displaced,
        });
      });
    });
  }

  stats.keep = finalRows.length;

  // -----------------------
  // Logging (concise)
  // -----------------------
  if (debug) {
    const ms = (performance.now() - t0).toFixed(1);

    console.log(
      `[DEDUP] done in ${ms}ms | input=${stats.input} | nameClusters=${stats.nameClusters} | ` +
      `coordsChanged=${stats.coordsChanged} | drops=${stats.dedupeDrops} | inactiveStatusDropped=${stats.inactiveStatusDropped} | ` +
      `displaced=${stats.displaced} | output=${stats.keep}`
    );

    if (sample.coordChanges.length) {
      console.log(`[DEDUP] sample coord changes (max ${logLimit}):`);
      sample.coordChanges.forEach((x) =>
        console.log(`  • ${x.name} (${x.sfId}) ${x.from} -> ${x.to} | ${x.reason}`)
      );
    }

    if (sample.drops.length) {
      console.log(`[DEDUP] sample drops (max ${logLimit}):`);
      sample.drops.forEach((x) =>
        console.log(
          `  • DROP ${x.name} (${x.sfId}) | BU=${x.bu} | coord=${x.refKey} | ${x.drop}` +
          (x.kept ? ` | kept=${x.kept}` : "") +
          (x.keptBecause ? ` | because=${x.keptBecause}` : "")
        )
      );
    }

    if (sample.disp.length) {
      console.log(`[DEDUP] sample displacements (max ${logLimit}):`);
      sample.disp.forEach((x) =>
        console.log(`  • DISP ${x.name} (${x.sfId}) @ ${x.coord} -> ${x.to} | ${x.reason}`)
      );
    }
  }

  return finalRows;
}
